import createDOMPurify from 'dompurify';

// Free-text metadata (asset descriptions, etc.) is mostly plain text, but many
// orgs have imported descriptions containing simple formatting HTML (<p>, <br>,
// <a href>, <i>, ...). Render that small allow-list of tags and show everything
// else literally: text such as "<some text I would like to see>", "< I think >"
// or newspaper-XML tags like "<pubdate>" must be visible, not swallowed.
export const ALLOWED_TAGS = [
    'a', 'p', 'br', 'b', 'strong', 'i', 'em', 'u', 'span', 'div',
    'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sup', 'sub', 'blockquote',
];

// tel: is kept so autolinked phone numbers still work.
const ALLOWED_URI_REGEXP = /^(?:https?|mailto|tel):/i;

// An opening/closing tag for an allowed element, e.g. <P>, </a>, <br/>, <a href='x' rel='nofollow'>.
const ALLOWED_TAG_RE = new RegExp(`^</?(?:${ALLOWED_TAGS.join('|')})(?=[\\s/>])[^<>]*>`, 'i');

// Escape every "<" that doesn't begin an allowed tag, so the HTML parser treats
// it as text. DOMPurify below remains the security boundary; this step only
// decides what is displayed literally versus interpreted as formatting.
export const escapeDisallowedTags = html => {
    const str = String(html ?? '');
    let out = '';

    for (let i = 0; i < str.length; i++) {
        const ch = str[i];
        if (ch === '<' && !ALLOWED_TAG_RE.test(str.slice(i, i + 2048))) {
            out += '&lt;';
        } else {
            out += ch;
        }
    }

    return out;
};

let purifier;

const getPurifier = () => {
    if (purifier) return purifier;

    // Dedicated instance so these hooks don't leak into other DOMPurify users.
    purifier = createDOMPurify(window);
    purifier.addHook('afterSanitizeAttributes', node => {
        if (node.tagName !== 'A') return;

        if (node.hasAttribute('href')) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer nofollow');
        }
    });

    return purifier;
};

export const sanitizeMetadataHtml = html => getPurifier().sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href'],
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    KEEP_CONTENT: true,
});
